import React, {createRef} from "react";
import {BaseComponent} from "@renta-apps/athenaeum-react-common";
import {
    Button,
    ButtonType,
    DateInput,
    Dropdown,
    DropdownAlign,
    DropdownOrderBy,
    Form,
    Icon,
    IconSize,
    IconStyle,
    Inline,
    JustifyContent, TextInput,
    ToolbarContainer,
    ToolbarRow,
    Checkbox
} from "@renta-apps/athenaeum-react-components";
import Localizer from "@/localization/Localizer";
import WorkOrderSearchInput from "@/components/WorkOrderSearchInput/WorkOrderSearchInput";
import Debouncer from "@/helpers/Debouncer";
import UserContext from "@/models/server/UserContext";
import CostPool from "@/models/server/CostPool";
import WorkOrderCreationRequestsToolbarModel from "@/pages/WorkOrderCreationRequests/WorkOrderCreationRequestsToolbar/WorkOrderCreationRequestsToolbarModel";

import styles from "./WorkOrderCreationRequestsToolbar.module.scss";

/**
 * Props for {@link WorkOrderCreationRequestsToolbar}.
 */
export interface IWorkOrderCreationRequestsToolbarProps {
    model: WorkOrderCreationRequestsToolbarModel;

    /**
     * Invoked when the {@link WorkOrderCreationRequestsToolbar} model changes.
     *
     * @param model The changed {@link WorkOrderCreationRequestsToolbar} model.
     */
    onChange(model: WorkOrderCreationRequestsToolbarModel): Promise<void>;

    /**
     * User context
     */
    userContext: UserContext;

    costPools: CostPool[];
    
    readonly?: boolean;
}

/** @private */
interface IWorkOrderCreationRequestsToolbarState {
    model: WorkOrderCreationRequestsToolbarModel;
}

/**
 * {@link WorkOrders} page toolbar.
 */
export default class WorkOrderCreationRequestsToolbar extends BaseComponent<IWorkOrderCreationRequestsToolbarProps, IWorkOrderCreationRequestsToolbarState> {

    public state: IWorkOrderCreationRequestsToolbarState = {
        model: this.props.model
    };

    private readonly _searchInputRef: React.RefObject<WorkOrderSearchInput> = createRef();
    private readonly _searchDebouncer: Debouncer = new Debouncer();

    private get readonly(): boolean {
        return (!!this.props.readonly);
    }
    private async processOnChange(invoke: boolean = false): Promise<void> {
        await this.setState({
            model: this.state.model,
        });

        if (invoke) {
            await this.props.onChange(this.state.model);
        }
    }

    private async setFromAsync(from: Date | null): Promise<void> {
        if (this.state.model.from !== from) {
            this.state.model.from = from;
            await this.processOnChange();
        }
    }

    private async setToAsync(to: Date | null): Promise<void> {
        if (this.state.model.to !== to) {
            this.state.model.to = to;
            await this.processOnChange();
        }
    }

    private async setShowDeletedAsync(showDeleted: boolean): Promise<void> {
        if (this.state.model.showDeleted !== showDeleted) {
            this.state.model.showDeleted = showDeleted;
            await this.processOnChange();
        }
    }

    private async selectCostPoolsAsync(costPools: CostPool[], userInteraction: boolean): Promise<void> {
        this.state.model.costPoolsFilter = costPools;

        if (userInteraction) {
            await this.processOnChange(true);
        }
    }

    private onSearchChangeAsync = async (search: string): Promise<void> => {
        if (this.state.model.search !== search) {
            this.state.model.search = search;

            this._searchDebouncer.debounce(
                async () => {
                    await this.processOnChange(true);
                },
                500
            );
        }
    }

    private async clearAsync(): Promise<void> {
        this.state.model = new WorkOrderCreationRequestsToolbarModel();

        await this.processOnChange(true);
    }

    public render(): React.ReactNode {
        return (
            <ToolbarContainer className={styles.workOrdersToolbar}>
                <ToolbarRow justify={JustifyContent.SpaceBetween}>
                    <Form inline>
                        <TextInput readonly={this.readonly}
                                   label={Localizer.tasksToolbarSearch}
                                   value={this.state.model.search ?? ""}
                                   onChange={(_, value) => this.onSearchChangeAsync(value)}
                        />

                        <Dropdown multiple autoGroupSelected small noValidate noSubtext noWrap noFilter
                                  label={Localizer.genericCostPoolsLanguageItemName}
                                  minWidth="180px"
                                  align={DropdownAlign.Left}
                                  orderBy={DropdownOrderBy.None}
                                  items={this.props.costPools}
                                  selectedItems={this.state.model.costPoolsFilter}
                                  disabled={(this.readonly) || (this.props.costPools.length <= 0)}
                                  onChange={(sender, _, userInteraction) => this.selectCostPoolsAsync(sender.selectedItems, userInteraction)}
                        />

                        <Inline className={styles.dateRangeContainer}>
                            <DateInput small rentaStyle
                                       id="from"
                                       label={Localizer.tasksToolbarDate}
                                       maxDate={new Date()}
                                       value={this.state.model.from || undefined}
                                       onChange={async (date) => await this.setFromAsync(date)}
                                       readonly={(this.readonly)}
                                       append={(
                                           <Icon name="fad fa-window-close"
                                                 style={IconStyle.Regular}
                                                 size={IconSize.Normal}
                                                 className={styles.clearDate}
                                                 tooltip={Localizer.genericClear}
                                                 onClick={() => this.setFromAsync(null)}
                                           />
                                       )}
                            />

                            <span className={styles.dateDelimiter}>-</span>

                            <DateInput small rentaStyle
                                       maxDate={new Date()}
                                       value={this.state.model.to || undefined}
                                       onChange={async (date) => await this.setToAsync(date)}
                                       readonly={(this.readonly)}
                                       append={(
                                           <Icon name="fad fa-window-close"
                                                 style={IconStyle.Regular}
                                                 size={IconSize.Normal}
                                                 className={styles.clearDate}
                                                 tooltip={Localizer.genericClear}
                                                 onClick={() => this.setToAsync(null)}
                                           />
                                       )}
                            />

                            <Checkbox label={Localizer.genericShowDeleted}
                                small rentaStyle
                                className={styles.checkbox}
                                value={this.state.model.showDeleted}
                                readonly={(this.readonly)}
                                id="showDeletedWorkOrderCreationRequests"
                                onChange={async (showDeleted) => await this.setShowDeletedAsync(showDeleted.value)}
                            />
                        </Inline>

                        <Inline className={styles.topMarginButton}>
                            <Button small
                                    label={Localizer.tasksToolbarSearch}
                                    icon={{name: "fas search"}}
                                    type={ButtonType.Orange}
                                    onClick={async () => await this.processOnChange(true)}
                                    disabled={(this.readonly)}
                            />

                            <Button small
                                    id="clearWorkOrderFiltersButton"
                                    title={Localizer.tasksToolbarClearFilters}
                                    icon={{name: "far history", size: IconSize.Large}}
                                    type={ButtonType.Info}
                                    onClick={async () => await this.clearAsync()}
                                    disabled={this.readonly}
                            />
                        </Inline>

                    </Form>
                </ToolbarRow>
            </ToolbarContainer>
        );
    }
}